<template>
  <main class="quote-page">
    <section v-if="loading" class="page-state">
      <p>Loading quote form...</p>
    </section>
    <section v-else-if="loadError" class="page-state">
      <h1>Quote form unavailable</h1>
      <p>We could not load the quote form. Please try again.</p>
      <button type="button" @click="getProject">Try again</button>
    </section>
    <section v-else-if="project && project.interactiveQuotesEnabled" class="quote-shell">
      <div v-if="submissionSucceeded" class="confirmation" role="status">
        <h1>Thank you</h1>
        <p>Thank you. Your quote was submitted.</p>
      </div>
      <template v-else>
        <h1>BE THE LIGHT.</h1>
        <p id="quote-intro" class="quote-intro">
          Submit a quote that speaks to the relationship of darkness and light.
          Accepted quotes will become part of the eternally evolving artwork
          <a href="https://dimonice.com/" rel="noopener noreferrer" target="_blank">DIMONice.com</a>,
          a work by artist Roz Dimon [originally created for
          <a
            href="https://icelandeclipse.com/"
            rel="noopener noreferrer"
            target="_blank"
          >ICELAND ECLIPSE 2026</a>.]
        </p>
        <form aria-describedby="quote-intro" novalidate @submit.prevent="submitForm">
          <div class="field">
            <label for="quote">
              QUOTE (Maximum: 74 characters with spaces)
              <span class="required-marker" aria-hidden="true">*</span>
              <span class="sr-only"> required</span>
            </label>
            <textarea
              id="quote"
              v-model="form.quote"
              :aria-describedby="quoteError ? 'quote-count quote-error' : 'quote-count'"
              :aria-invalid="Boolean(quoteError)"
              name="quote"
              required
            ></textarea>
            <p id="quote-count" class="quote-count">{{ quoteLength }} / 74 characters</p>
            <p v-if="quoteError" id="quote-error" class="field-error">{{ quoteError }}</p>
          </div>

          <div class="field">
            <label for="quote-author">AUTHOR OF QUOTE <span>(optional)</span></label>
            <input
              id="quote-author"
              v-model="form.quoteAuthor"
              name="quoteAuthor"
              type="text"
            />
          </div>

          <div class="field">
            <label for="submitter-name">
              TYPE YOUR NAME if you want it shown as the submitter of the quote
              <span>(optional)</span>
            </label>
            <input
              id="submitter-name"
              v-model="form.submitterName"
              name="submitterName"
              type="text"
            />
          </div>

          <div class="field">
            <label for="submitter-email">
              Your email
              <span class="required-marker" aria-hidden="true">*</span>
              <span class="sr-only"> required</span>
            </label>
            <input
              id="submitter-email"
              v-model="form.submitterEmail"
              :aria-describedby="emailError ? 'email-error' : null"
              :aria-invalid="Boolean(emailError)"
              name="submitterEmail"
              required
              type="email"
            />
            <p v-if="emailError" id="email-error" class="field-error">{{ emailError }}</p>
          </div>

          <fieldset
            class="field"
            :aria-describedby="acknowledgmentError
              ? 'acknowledgment-help acknowledgment-error' : 'acknowledgment-help'"
            :aria-invalid="Boolean(acknowledgmentError)"
          >
            <legend>
              May we list your name as a quote contributor in the artwork’s information panel?
              <span class="required-marker" aria-hidden="true">*</span>
              <span class="sr-only"> required</span>
            </legend>
            <p id="acknowledgment-help">
              This does not affect the quote or its author credit if the quote is approved.
            </p>
            <div class="acknowledgment-options">
              <label for="acknowledgment-yes">
                <input
                  id="acknowledgment-yes"
                  v-model="form.allowAcknowledgment"
                  :value="true"
                  name="allowAcknowledgment"
                  required
                  type="radio"
                />
                Yes
              </label>
              <label for="acknowledgment-no">
                <input
                  id="acknowledgment-no"
                  v-model="form.allowAcknowledgment"
                  :value="false"
                  name="allowAcknowledgment"
                  required
                  type="radio"
                />
                No
              </label>
            </div>
            <p
              v-if="acknowledgmentError"
              id="acknowledgment-error"
              class="field-error"
            >{{ acknowledgmentError }}</p>
          </fieldset>

          <div
            v-if="captchaEnabled"
            class="field captcha-field"
            :aria-describedby="captchaDescriptionIds"
            aria-labelledby="captcha-label"
            role="group"
          >
            <span id="captcha-label">
              Verification
              <span class="required-marker" aria-hidden="true">*</span>
              <span class="sr-only"> required</span>
            </span>
            <TurnstileWidget
              v-if="!captchaLoadFailed"
              ref="turnstile"
              :site-key="turnstileSiteKey"
              @error="handleCaptchaError"
              @expired="clearCaptcha"
              @verified="setCaptcha"
            />
            <div v-else class="captcha-retry" role="alert">
              <p id="captcha-load-error">Verification could not load.</p>
              <button type="button" @click="retryCaptcha">Retry verification</button>
            </div>
            <p v-if="captchaError" id="captcha-error" class="field-error">{{ captchaError }}</p>
          </div>

          <p v-if="submissionError" class="submission-error" role="alert">
            {{ submissionError }}
          </p>
          <button :disabled="sending" type="submit">
            {{ sending ? 'Submitting...' : 'Submit quote' }}
          </button>
        </form>
      </template>
    </section>
    <section v-else class="page-state">
      <h1>Quote submissions unavailable</h1>
      <p>Quote submissions are not available for this project.</p>
    </section>
  </main>
</template>

<script>
import TurnstileWidget from '@/components/TurnstileWidget.vue';

export default {
  name: 'Quotes',
  components: {
    TurnstileWidget,
  },
  data() {
    return {
      loading: true,
      loadError: false,
      project: null,
      submitted: false,
      captchaToken: '',
      captchaLoadFailed: false,
      sending: false,
      submissionError: '',
      submissionSucceeded: false,
      form: {
        quote: '',
        quoteAuthor: '',
        submitterName: '',
        submitterEmail: '',
        allowAcknowledgment: null,
      },
    };
  },
  computed: {
    captchaEnabled() {
      if (typeof window.dimonScapeCaptchaEnabled === 'boolean') {
        return window.dimonScapeCaptchaEnabled;
      }
      return process.env.VUE_APP_QUOTE_CAPTCHA_ENABLED === 'true';
    },
    turnstileSiteKey() {
      return process.env.VUE_APP_TURNSTILE_SITE_KEY || '';
    },
    quoteLength() {
      return this.form.quote.trim().length;
    },
    quoteError() {
      if (!this.submitted) return '';
      if (!this.form.quote.trim()) return 'Enter a quote.';
      if (this.quoteLength > 74) return 'Quote must be 74 characters or fewer.';
      return '';
    },
    emailError() {
      if (!this.submitted) return '';
      const email = this.form.submitterEmail.trim();
      if (!email) return 'Enter your email address.';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address.';
      return '';
    },
    acknowledgmentError() {
      if (!this.submitted || typeof this.form.allowAcknowledgment === 'boolean') return '';
      return 'Choose yes or no.';
    },
    captchaError() {
      if (!this.captchaEnabled || !this.submitted || this.captchaToken.trim()) return '';
      return 'Complete the verification.';
    },
    captchaDescriptionIds() {
      return [
        this.captchaLoadFailed ? 'captcha-load-error' : '',
        this.captchaError ? 'captcha-error' : '',
      ].filter(id => id).join(' ') || null;
    },
    hasValidationErrors() {
      return Boolean(
        this.quoteError
        || this.emailError
        || this.acknowledgmentError
        || this.captchaError,
      );
    },
  },
  methods: {
    setCaptcha(token) {
      this.captchaToken = typeof token === 'string' ? token : '';
    },
    clearCaptcha() {
      this.captchaToken = '';
    },
    handleCaptchaError() {
      this.clearCaptcha();
      this.captchaLoadFailed = true;
    },
    retryCaptcha() {
      this.captchaLoadFailed = false;
    },
    resetCaptcha() {
      this.clearCaptcha();
      if (this.$refs.turnstile) this.$refs.turnstile.reset();
    },
    submitForm() {
      if (this.sending) return;
      this.submitted = true;
      this.submissionError = '';
      if (this.hasValidationErrors) return;

      this.sending = true;
      const payload = {
        quote: this.form.quote.trim(),
        quoteAuthor: this.form.quoteAuthor.trim(),
        submitterName: this.form.submitterName.trim(),
        submitterEmail: this.form.submitterEmail.trim(),
        allowAcknowledgment: this.form.allowAcknowledgment,
      };
      if (this.captchaEnabled) payload.captchaToken = this.captchaToken.trim();

      this.axios.post(
        `${this.$API}/projects/${this.project.id}/quote-submissions`,
        payload,
      ).then(() => {
        this.sending = false;
        if (this.captchaEnabled) this.clearCaptcha();
        this.submissionSucceeded = true;
      }).catch(() => {
        this.sending = false;
        this.submissionError = 'We could not submit your quote. Please try again.';
        if (this.captchaEnabled) this.resetCaptcha();
      });
    },
    normalizeDomain(value) {
      let domain = String(value || '').trim().toLowerCase();
      domain = domain.replace(/^https?:\/\//, '').split('/')[0].replace(/\.$/, '');
      if (domain.split('.')[0] === 'www') {
        domain = domain.split('.').slice(1).join('.');
      }
      return domain;
    },
    projectHasDomain(project, domain) {
      return String(project.domain || '')
        .split(',')
        .map(entry => this.normalizeDomain(entry))
        .filter(entry => entry)
        .some(entry => entry === domain);
    },
    projectDomain() {
      return this.normalizeDomain(window.location.host);
    },
    getProject() {
      const domain = this.projectDomain();
      this.loading = true;
      this.loadError = false;
      this.project = null;
      this.axios.get(`${this.$API}/projects?domain_contains=${encodeURIComponent(domain)}`)
        .then((response) => {
          const projects = Array.isArray(response.data) ? response.data : [];
          const exactMatches = projects.filter(
            project => this.projectHasDomain(project, domain),
          );
          if (exactMatches.length === 1) {
            [this.project] = exactMatches;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.loadError = true;
        });
    },
  },
  created() {
    this.getProject();
  },
};
</script>

<style lang="scss" scoped>
.quote-page {
  background: #fff;
  box-sizing: border-box;
  color: #373b4d;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4.5rem 1.5rem 5rem;
  text-align: left;

  section {
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    box-sizing: border-box;
    margin: 0 auto;
    max-width: 600px;
    padding: 0;
    width: 100%;
  }

  h1 {
    color: #373b4d;
    font-size: 2rem;
    font-weight: 400;
    letter-spacing: .01em;
    line-height: 1.2;
    margin: 0 0 .75rem;
  }

  .quote-intro {
    color: #626570;
    line-height: 1.6;
    margin: 0 0 2.75rem;
  }

  .field {
    border: 0;
    margin: 0 0 1.75rem;
    padding: 0;
  }

  label,
  legend,
  .field > span {
    color: #373b4d;
    display: block;
    font-size: .95rem;
    font-weight: 600;
    letter-spacing: .01em;
    line-height: 1.45;
    margin-bottom: .6rem;
  }

  label span {
    color: #6b6d76;
    font-weight: 400;
  }

  .required-marker {
    color: #a13232;
    display: inline;
    font-weight: 600;
    margin-left: .15em;
  }

  .sr-only {
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  input[type="text"],
  input[type="email"],
  textarea {
    background: #fff;
    border: 1px solid #373b4d;
    border-radius: 0;
    box-sizing: border-box;
    color: #373b4d;
    font-family: Georgia, Cambria, "Times New Roman", serif;
    font-size: 1rem;
    font-style: italic;
    line-height: 1.5;
    min-height: 48px;
    padding: .8rem .9rem;
    width: 100%;
  }

  textarea {
    min-height: 8rem;
    resize: vertical;
  }

  input[type="text"]:focus,
  input[type="email"]:focus,
  textarea:focus {
    border-color: #2a8b98;
    box-shadow: 0 0 0 2px rgba(42, 139, 152, .2);
    outline: 0;
  }

  input[aria-invalid="true"],
  textarea[aria-invalid="true"] {
    border-color: #a13232;
  }

  .acknowledgment-options {
    display: flex;
    flex-wrap: nowrap;
    gap: 1rem;
    max-width: 100%;
  }

  .acknowledgment-options label {
    align-items: center;
    display: inline-flex;
    flex: 0 0 auto;
    font-weight: 400;
    gap: .5rem;
    margin: .15rem 0;
    min-height: 44px;
    min-width: 44px;
    width: auto;
  }

  input[type="radio"] {
    accent-color: #2a8b98;
    height: 1.1rem;
    margin: 0;
    width: 1.1rem;
  }

  .quote-count {
    color: #6b6d76;
    font-size: .875rem;
    margin: .375rem 0 0;
    text-align: right;
  }

  .field-error,
  .submission-error {
    color: #a13232;
    font-size: .9rem;
    line-height: 1.45;
    margin: .375rem 0 0;
  }

  .captcha-field {
    min-width: 0;
  }

  .captcha-retry p {
    margin: 0 0 .75rem;
  }

  .turnstile-widget {
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }

  button {
    background: #373b4d;
    border: 1px solid #373b4d;
    border-radius: 0;
    color: #fff;
    cursor: pointer;
    font: inherit;
    font-size: .95rem;
    font-weight: 600;
    min-height: 44px;
    padding: .75rem 1.75rem;
  }

  button:hover:not(:disabled) {
    background: #2e3140;
  }

  button:focus {
    outline: 2px solid #2a8b98;
    outline-offset: 3px;
  }

  button:disabled {
    cursor: wait;
    opacity: .65;
  }

  .confirmation {
    text-align: center;
  }

  .confirmation p,
  .page-state p {
    line-height: 1.6;
    margin: 0 0 1.5rem;
  }
}

@media (max-width: 600px) {
  .quote-page {
    padding: 2rem 1rem 3rem;

    h1 {
      font-size: 1.75rem;
    }

    .quote-intro {
      margin-bottom: 2rem;
    }

    button {
      width: 100%;
    }
  }
}
</style>
